import { LitElement, html, css } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import scrollLeftSvg from '../assets/scroll-button-left.svg'
import scrollRightSvg from '../assets/scroll-button-right.svg'
import type { AgentSummarySource } from "../models/AgentSummary";

@customElement("facs-sources")
export class Sources extends LitElement {
    @property()
    sources: AgentSummarySource[] | undefined;

    @query('#sources-scroll-container')
    private scrollContainer!: HTMLElement;

    @query('#sources-scroll-start')
    private scrollStart!: HTMLButtonElement;

    @query('#sources-scroll-end')
    private scrollEnd!: HTMLButtonElement;

    /**
     * Updates the visibility of the horizontal scroll buttons
     * based on the current scroll position of the sources container.
     *
     * - Hides both buttons if the content does not overflow horizontally
     * - Hides the "start" button when scrolled fully to the left
     * - Hides the "end" button when scrolled fully to the right
     */
    private updateScrollButtons() {
        if (!this.scrollContainer) return;

        const canScroll =
            this.scrollContainer.scrollWidth > this.scrollContainer.clientWidth;

        const scrollLeft = this.scrollContainer.scrollLeft;
        const maxScrollLeft =
            this.scrollContainer.scrollWidth - this.scrollContainer.clientWidth;

        if (!canScroll) {
            this.scrollStart.style.display = 'none';
            this.scrollEnd.style.display = 'none';
            return;
        }

        this.scrollStart.style.display = scrollLeft <= 0 ? 'none' : 'block';
        this.scrollEnd.style.display =
            scrollLeft >= maxScrollLeft - 1 ? 'none' : 'block';
    }

    /**
     * Calculates the horizontal scroll distance needed to advance
     * exactly one source item.
     *
     * This is based on:
     * - The rendered width of the first source card
     * - The grid column gap applied to the container
     *
     * Using measured values keeps scrolling in sync with responsive
     * grid sizing and avoids hardcoded pixel values.
     */
    private getScrollStep(): number {
        const firstItem = this.scrollContainer.firstElementChild as HTMLElement | null;
        if (!firstItem) return 0;

        const itemWidth = firstItem.getBoundingClientRect().width;

        const gap = parseFloat(
            getComputedStyle(this.scrollContainer).columnGap || '0'
        );

        return itemWidth + gap;
    }

    /**
     * Initializes scroll behavior and event listeners once the component
     * has rendered and DOM references are available.
     *
     * - Wires up left/right scroll buttons to move one source at a time
     * - Updates button visibility on scroll and window resize
     * - Sets the initial button state
     */
    protected firstUpdated() {
        this.scrollEnd.addEventListener('click', () => {
            const step = this.getScrollStep();
            this.scrollContainer.scrollBy({
                left: step,
                behavior: 'smooth'
            });
        });

        this.scrollStart.addEventListener('click', () => {
            const step = this.getScrollStep();
            this.scrollContainer.scrollBy({
                left: -step,
                behavior: 'smooth'
            });
        });

        this.scrollContainer.addEventListener('scroll', () =>
            this.updateScrollButtons()
        );

        window.addEventListener('resize', () =>
            this.updateScrollButtons()
        );

        this.updateScrollButtons();
    }

    render() {
        if (this.sources == undefined) return;

        const hasMultiple = this.sources.length > 1;

        return html`
            <div class=sources-container>
                    <hr/>
                    <div class="sources" id=sources-scroll-container>

                    ${this.sources?.map(source => html`
                        <facs-source .source=${source} ?multiple=${hasMultiple}></facs-source>
                    `)}
                    </div>
                <button class="scroll-button" id="sources-scroll-start">
                     <img src="${scrollLeftSvg}" class="icon" alt="scroll left" />
                </button>
                <button class="scroll-button" id="sources-scroll-end">
                    <img src="${scrollRightSvg}" class="icon" alt="scroll right" />
                </button>
            </div>
        `;
    }

    static styles = css`
        .sources-container {
            position: relative;
            --source-gap: 2rem;

            border-left: 0.25rem solid #E91F00;

            .sources {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
                gap: var(--source-gap);
                padding: 3rem 2rem;
                scrollbar-width: none;
                overflow-x: auto;
                -ms-overflow-style: none;

                &::-webkit-scrollbar {
                    display: none;
                }
           
                a {
                    text-decoration: none;
                    color:inherit;
                }
            }

            .sources:has(> :nth-child(4)) {
                grid-template-columns: none;
                grid-auto-flow: column;
                grid-auto-columns: calc((100% - 4rem) / 3);
                overflow-x: auto;
                overscroll-behavior-x: contain;
                scroll-snap-type: x mandatory;
            }

            hr {
                margin: 0 2rem;
                border: 0.0625rem solid #C4D1D7;
            }

            .scroll-button {
                background: none;
                border: 0;
                padding: 0;
                position: absolute;
                top: 50%;
                transform: translateY(-43%);

                &#sources-scroll-start {
                    left: 0;
                }

                &#sources-scroll-end {
                    right: 0;
                }

                > img {
                    height: 6.25rem;
                }
            }

            @media (max-width: 840px) {
                .sources {
                    grid-auto-flow: unset !important;
                    grid-template-columns: 1fr !important;
                    grid-auto-columns: unset !important;
                    grid-auto-rows: 1fr !important;
                    gap: 1rem !important;
                    padding: 2rem 1rem !important;
                }

                .sources > * {
                    max-width: none !important;
                }

                hr {
                    margin: 0 1rem !important;
                }

                .scroll-button {
                    display: none !important;
                }
            }
        }
    `;
}